import { useState } from "react";
import { LocationPros } from "../../../../types/baseTypes";
import { useTranslation } from "react-i18next";
import { useGetLocationItemsByLocationIdQuery } from "../../../../api/locations/locationsApiSlice";
import ExpandIcon from "./../../../../assets/icons/expand_icon.png";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import FullLockerView from "../components/locationFromBottomBar/FullLockerView";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { formatOpeningHour, shouldShowOpeningHours } from "./visibility";
import { mapEmbedSrc } from "../../../../util/mapEmbed";
import { streetLineOf } from "../../../../util/locationAddress";
interface Props {
  selectedLocation: LocationPros | null;
}
function LocationSelectionDetails({ selectedLocation }: Props) {
  const { t } = useTranslation();
  const { isLoading } = useGetLocationItemsByLocationIdQuery(
    { locationId: selectedLocation?.id! },
    { skip: !selectedLocation?.id }
  );

  const [openImage, setOpenImage] = useState(false);

  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
  const showOpeningHours = 
    (tenantConfig?.showOpeningHoursOnLocationsPage ?? true) &&
    shouldShowOpeningHours(selectedLocation);

  // Prepare ordered opening hours Monday -> Sunday
  const weekdaysOrdered: string[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  interface OpeningHourRow {
    weekday: string;
    fromTime: string;
    toTime: string;
    hasHours: boolean;
  }
  const orderedOpeningHours: OpeningHourRow[] = [];
  if (showOpeningHours) {
    weekdaysOrdered.forEach((weekday) => {
      const dayEntry = selectedLocation?.openingHours?.find((h) => h.weekday === weekday);
      const fromHour = dayEntry?.times?.fromHour;
      const toHour = dayEntry?.times?.toHour;
      if (fromHour === undefined || toHour === undefined) return;
      if (fromHour === 0 && toHour === 0) return;
      const fromTime = formatOpeningHour(fromHour, "H:mm");
      const toTime = formatOpeningHour(toHour, "H:mm");
      orderedOpeningHours.push({ weekday, fromTime, toTime, hasHours: true });
    });
  }

  // Coordinates when the location has them, its address otherwise, no map
  // without either — never `q=null,null` (util/mapEmbed.ts).
  const mapSrc = mapEmbedSrc(selectedLocation?.address);
  const streetLine = streetLineOf(selectedLocation);

  const handleOpenImage = () => setOpenImage(true);

  if (isLoading) {
    return <LoaderOverlay open={isLoading} />;
  }
  return (
    <div className="w-full max-w-screen-sm mx-auto pb-8">
      {/* Map */}
          {mapSrc && (
            <div className="w-full h-36 border border-slate-300 rounded-2xl p-0.5 my-4">
              <iframe
                className="h-36 rounded-xl"
                title="Location Map"
                src={mapSrc}
                style={{ width: "100%", height: "100%", border: "0" }}
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          )}
          {/* item details */}
          <div className="w-full flex items-start mt-2 gap-4">
            {selectedLocation && selectedLocation.locationImages.length > 0 && (
              <div className="w-1/2 relative">
                <button
                  onClick={handleOpenImage}
                  className="absolute bg-black/50 h-6 w-6
                  rounded-md right-2 top-2 flex items-center justify-center"
                >
                  <img
                    className="h-4 object-cover"
                    src={ExpandIcon}
                    alt="locker"
                  />
                </button>
                {/* Cut to a square, the crop the admin preview shows (ADR-0003
                    amendment 2026-10-19); the full photo opens on tap. */}
                <img
                  className="w-full aspect-square object-cover rounded-lg"
                  src={selectedLocation?.locationImages[0]}
                  alt="locker"
                />
              </div>
            )}
            <div className="ml-2 ">
              <div className="">
                <p className="font-bold text-base">
                  {t("location_from_bottom_bar.location_info")}
                </p>
                <p className="font-normal text-slate-800 text-base">
                  {streetLine}
                </p>
                <p className="font-bold text-base">
                  {selectedLocation?.address?.postalCode}{" "}
                  {selectedLocation?.address?.city}
                </p>
              </div>

              {showOpeningHours && (
                <div className="mt-2">
                  <p className="font-normal text-slate-800 text-base">
                    {t("location_from_bottom_bar.opening_hours")}:
                  </p>
                  <div className="flex flex-col w-full mt-1 gap-0.5">
                    {orderedOpeningHours.map((oh) => (
                      <div key={oh.weekday} className="flex items-center">
                        <div className="w-[50px] font-semibold pr-1 text-left text-base">
                          {t(`common.weekdays.${oh.weekday}`)}
                        </div>
                        <div className="text-base flex-1">
                          {oh.fromTime}
                          <span className="mx-1">-</span>
                          {oh.toTime}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
      {/* Items intentionally removed (unused/commented code) */}
      {selectedLocation && selectedLocation.locationImages.length > 0 && (
        <FullLockerView
          setOpenImage={setOpenImage}
          image={selectedLocation?.locationImages[0]!}
          openImage={openImage}
        />
      )}
    </div>
  );
}

export default LocationSelectionDetails;
